import * as React from 'react';
import { useRef } from 'react';
import classnames from '../utils/classnames.tsx';
import { Slot } from '@radix-ui/react-slot';
import { mergeRefs } from '../utils/mergeRefs.tsx';
import useStickyState from '../utils/useStickyState.tsx';

export interface RowControlButtonProps extends React.ComponentPropsWithRef<'button'> {
    active?: boolean;
    asChild?: boolean;
}

interface TableProps extends React.ComponentPropsWithRef<'table'> {
    // wraps the table in TableScrollWrapper so wide tables scroll inside their own box instead of the page
    scrollable?: boolean;
}

const Table = ({ className, scrollable, ...props }: TableProps) => {
    const table = (
        <table
            className={classnames(
                'table-fixed border-separate border-spacing-0 border border-line-weak border-solid rounded-lg',
                '[&:not(:has(thead))>tbody>tr:first-child>td:first-child]:rounded-tl-lg',
                '[&:not(:has(thead))>tbody>tr:first-child>td:last-child]:rounded-tr-lg',
                className
            )}
            {...props}
        />
    );

    return scrollable ? <TableScrollWrapper className="shrink-0">{table}</TableScrollWrapper> : table;
};

interface TableHead extends React.ComponentPropsWithRef<'thead'> {
    sticky?: boolean;
}

const TableHead = ({ className, sticky, ...props }: TableHead) => {
    const ref = useRef<HTMLTableSectionElement>(null);
    const { top } = useStickyState(ref, !!sticky);
    return (
        <thead
            className={classnames(
                sticky && 'sticky top-0 z-10',
                sticky && top && 'shadow [clip-path:inset(0_0_-15px_0)]',
                className
            )}
            {...props}
            data-state={sticky ? (top ? 'sticky' : 'static') : undefined}
            ref={mergeRefs(ref, props.ref)}
        />
    );
};

const TableBody = ({ className, ...props }: React.ComponentPropsWithRef<'tbody'>) => {
    return <tbody className={className} {...props} />;
};

const TableRow = ({ className, ...props }: React.ComponentPropsWithRef<'tr'>) => {
    return (
        <tr
            className={classnames('group border-b border-line-weak border-solid last:border-none', className)}
            {...props}
        />
    );
};

interface TableCellProps extends React.ComponentPropsWithRef<'td'> {
    sticky?: boolean | 'left' | 'right';
}

const TableCell = ({ className, sticky, ...props }: TableCellProps) => {
    const ref = useRef<HTMLTableCellElement>(null);
    const { left, right } = useStickyState(ref, !!sticky);
    return (
        <td
            className={classnames(
                'px-4 py-2 h-14 bg-surface-weak text-content-strong align-middle text-sm border-b border-line-weak border-solid group-last:border-b-0 group-last:first:rounded-bl-lg group-last:last:rounded-br-lg',
                sticky ? (sticky === 'right' ? 'sticky right-0' : 'sticky left-0') : '',
                sticky === 'right' && right && 'shadow [clip-path:inset(0_0_0_-15px)]',
                sticky && sticky !== 'right' && left && 'shadow [clip-path:inset(0_-15px_0_0)]',
                className
            )}
            {...props}
            data-state={sticky ? (left || right ? 'sticky' : 'static') : undefined}
            ref={mergeRefs(ref, props.ref)}
        />
    );
};

interface TableHeadCellProps extends React.ComponentPropsWithRef<'th'> {
    sticky?: boolean | 'left' | 'right';
}

const TableHeadCell = ({ className, sticky, ...props }: TableHeadCellProps) => {
    const ref = useRef<HTMLTableHeaderCellElement>(null);
    const { left, right } = useStickyState(ref, !!sticky);

    return (
        <th
            className={classnames(
                'p-4 py-3 whitespace-nowrap text-content-medium align-middle text-left text-sm border-b border-line-weak border-solid bg-surface-strong first:rounded-tl-lg last:rounded-tr-lg font-medium',
                sticky ? (sticky === 'right' ? 'sticky right-0' : 'sticky left-0') : '',
                sticky === 'right' && right && 'shadow [clip-path:inset(0_0_0_-15px)]',
                sticky && sticky !== 'right' && left && 'shadow [clip-path:inset(0_-15px_0_0)]',
                className
            )}
            {...props}
            data-state={sticky ? (left || right ? 'sticky' : 'static') : undefined}
            ref={mergeRefs(ref, props.ref)}
        />
    );
};

export default Object.assign(Table, {
    Head: TableHead,
    Body: TableBody,
    Row: TableRow,
    Cell: TableCell,
    HeadCell: TableHeadCell,
});

export const TableScrollWrapper = ({ children, className, ...props }: React.ComponentPropsWithRef<'div'>) => {
    return (
        <div
            className={classnames(
                // the wrapper is the frame; the child variant outranks the table's own border utilities, which a
                // merged `border-none` class does not
                'overflow-x-auto overflow-y-auto border-line-weak border border-solid rounded-lg [&>table]:border-0',
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
};

export const RowControlIconButton = ({ className, active = false, asChild, ...props }: RowControlButtonProps) => {
    const Comp = asChild ? Slot : 'button';
    return (
        <Comp
            className={classnames(
                'flex items-center justify-center gap-2 rounded transition-colors duration-200 hover:bg-surface-strong-hover active:bg-surface-strong-active w-8 h-8 cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-50 disabled:pointer-events-none data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            {...props}
        />
    );
};

export const RowControlButton = ({ className, active = false, asChild, children, ...props }: RowControlButtonProps) => {
    const Comp = asChild ? Slot : 'button';
    return (
        <Comp
            className={classnames(
                'flex items-center justify-center gap-2 rounded transition-colors duration-200 hover:bg-surface-strong whitespace-nowrap px-2 h-8 cursor-pointer text-sm font-medium text-content-strong hover:text-content-strong disabled:opacity-50 disabled:pointer-events-none data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong',
                active &&
                    '!bg-surface-brand-medium-default !text-content-brand-strong hover:!bg-surface-brand-medium-hover active:!bg-surface-brand-medium-active',
                className
            )}
            {...props}
        >
            {children}
        </Comp>
    );
};
